import { times, max, orderBy, values, keys, difference, isEqual } from 'lodash';
import { Question } from '../../question.class';
import {
  LightWeightObjectChangeTracker,
  SetOptions
} from '@schoolbelle/common/object-change-tracker';
import { TableInterface } from './table.interface';
import { Subscription } from 'rxjs';
import { Col } from './col';
import { Row } from './row';
import { Cell } from './cell';

export { Col, Row, Cell };
export class TableHandler extends LightWeightObjectChangeTracker
  implements TableInterface {
  private _columns: { [id: string]: Col } = {};
  private _rows: { [id: string]: Row } = {};
  private _cells: { [id: string]: Cell } = {};
  private _focus: { colId: string; rowId: string } = {
    colId: undefined,
    rowId: undefined
  };
  get focus(): { colId: string; rowId: string } {
    return this._focus;
  }
  get columns(): Col[] {
    let columns = orderBy(values(this._columns), 'priority', 'asc');
    // if (this.showYHeader) columns = columns.slice(1);
    return columns;
  }
  get rows(): Row[] {
    let rows = orderBy(values(this._rows), 'priority', 'asc');
    // if (this.showXHeader) rows = rows.slice(1);
    return rows;
  }
  get cells(): Cell[] {
    return values(this._cells);
  }
  get xLength(): number {
    return this.columns.length;
  }
  set xLength(v: number) {
    if (this.showYHeader === false) v = v + 1;
    this.changeColLength(v);
  }
  get yLength(): number {
    return this.rows.length;
  }
  set yLength(v: number) {
    if (this.showXHeader === false) v = v + 1;
    this.changeRowLength(v);
  }
  get showXHeader(): boolean {
    return this.get('showXHeader') ? true : false;
  }
  set showXHeader(v: boolean) {
    this.set('showXHeader', v);
  }
  get showYHeader(): boolean {
    return this.get('showYHeader') ? true : false;
  }
  set showYHeader(v: boolean) {
    this.set('showYHeader', v);
  }
  subscriptions: Subscription[] = [];
  constructor(public question: Question, paths: string[]) {
    super(question, paths);
    this.subscriptions.push(
      this.question
        .getChildTracker('options')
        .onContentUpdate.subscribe(() => this.refreshTable())
    );
  }
  destroy() {
    this.question.set('table', null, { push: 'none' });
    this.destroyTracker();
  }
  toggleFocus(
    colId: string = undefined,
    rowId: string = undefined,
    bool: boolean = undefined
  ) {
    if (typeof bool === 'undefined')
      bool = !isEqual(this._focus, { colId, rowId });
    if (bool) {
      this._focus = { colId, rowId };
    } else {
      this._focus = { colId: undefined, rowId: undefined };
    }
  }
  private changeColLength(n: number) {
    let diff = n - this.columns.length;
    if (diff > 0) {
      times(diff, i =>
        this.addColumn(this.columns.length, undefined, { push: 'none' })
      );
    } else if (diff < 0) {
      times(Math.abs(diff), i =>
        this.removeColumn(this.columns.pop(), { push: 'none' })
      );
    }
    this.question.getChildTracker('options').notifyChangeThruTree([]);
  }
  private changeRowLength(n: number) {
    if (this.showYHeader === false) n = n - 1;
    let diff = n - this.rows.length;
    if (diff > 0) {
      times(diff, i =>
        this.addRow(this.rows.length, undefined, { push: 'none' })
      );
    } else if (diff < 0) {
      times(Math.abs(diff), i =>
        this.removeRow(this.rows.pop(), { push: 'none' })
      );
    }
    this.question.getChildTracker('options').notifyChangeThruTree([]);
  }
  private getMaxPriority(list: (Col | Row)[]): number {
    if (list.length > 0) {
      return max(list.map(item => item.priority));
    } else {
      return -1;
    }
  }
  private refreshTable(setOptions?: SetOptions) {
    let col_ids_new = keys(this.question.get('table.columns')),
      col_ids_old = keys(this._columns);
    difference(col_ids_new, col_ids_old).forEach(id => {
      this._columns[id] = new Col(this, id);
    });
    difference(col_ids_old, col_ids_new).forEach(id => {
      let col = this._columns[id];
      if (col) {
        this.removeColumn(col, { push: 'none' });
      }
    });

    let row_ids_new = keys(this.question.get('table.rows')),
      row_ids_old = keys(this._rows);
    difference(row_ids_new, row_ids_old).forEach(id => {
      this._rows[id] = new Row(this, id);
    });
    difference(row_ids_old, row_ids_new).forEach(id => {
      let row = this._rows[id];
      if (row) {
        this.removeRow(row, { push: 'none' });
      }
    });
    this.refreshCells(setOptions);
  }
  private refreshCells(setOptions?: SetOptions) {
    let colIds = this.columns.map(c => c.id).slice(1),
      rowIds = this.rows.map(r => r.id).slice(1);

    // delete !
    this.cells
      .filter(c => !colIds.includes(c.colId) || !rowIds.includes(c.rowId))
      .forEach(c => this.removeCell(c, { push: 'none' }));

    colIds.forEach(colId =>
      rowIds.forEach(rowId => {
        let found: Cell = this.cells.find(
          c => c.colId === colId && c.rowId === rowId
        );
        if (!found) {
          // insert !
          let col = this.columns.find(c => c.id === colId);
          let row = this.columns.find(c => c.id === colId);
          this.addCell(colId, rowId, `${col.text} ${row.text}`, {
            push: 'none'
          });
        }
      })
    );

    // notify !
    this.notifyChangeThruTree([], setOptions);
  }
  genId(list: any[], prefix = 'id_'): string {
    return prefix + (list.length + 1);
  }
  addColumn(
    pos: number = this.getMaxPriority(this.columns) + 1,
    id: string = this.genId(this.columns),
    setOptions?: SetOptions
  ) {
    let col = (this._columns[id] = new Col(this, id));
    col.setPriority(pos, undefined, undefined, { push: 'none' });
    this.refreshCells({ push: 'none' });
    this.notifyChangeThruTree([], setOptions);
    return col;
  }
  removeColumn(col: Col, setOptions?: SetOptions) {
    this.columns
      .filter(c => c.priority > col.priority)
      .forEach(c => c.set('priority', c.priority - 1, { push: 'none' }));
    col.destroyTracker();
    delete this._columns[col.id];
    this.set(['columns', col.id], null, { push: 'none' });
    this.refreshCells({ push: 'none' });
    this.notifyChangeThruTree([], setOptions);
  }
  addRow(
    pos: number = this.getMaxPriority(this.rows) + 1,
    id: string = this.genId(this.rows),
    setOptions?: SetOptions
  ) {
    let row = (this._rows[id] = new Row(this, id));
    row.setPriority(pos, undefined, undefined, { push: 'none' });
    this.refreshCells({ push: 'none' });
    this.notifyChangeThruTree([], setOptions);
    return row;
  }
  removeRow(row: Row, setOptions?: SetOptions) {
    this.rows
      .filter(c => c.priority > row.priority)
      .forEach(r => r.set('priority', r.priority - 1, { push: 'none' }));
    row.destroyTracker();
    delete this._rows[row.id];
    this.set(['rows', row.id], null, { push: 'none' });
    this.refreshCells({ push: 'none' });
    this.notifyChangeThruTree([], setOptions);
  }
  addCell(
    colId: string,
    rowId: string,
    text?: string,
    setOptions?: SetOptions
  ) {
    if (this.cells.find(c => c.colId === colId && c.rowId === rowId))
      throw new Error('cell already exists');
    const id = `${colId}-${rowId}`;

    // add option
    let option =
      this.question._options[id] ||
      this.question.addOption(
        { id, text, quota: this.question.canHaveQuotaOptions ? 1 : undefined },
        { push: 'none' }
      );
    // let option = this.question.addOption({id, text, quota:1}, {push:'none'});

    // add cell
    let cell = (this._cells[id] = new Cell(this, option));

    // push
    this.notifyChangeThruTree([], setOptions);

    return cell;
  }
  removeCell(cell: Cell, setOptions?: SetOptions) {
    // send destory signal
    cell.destroy();

    // remove cell
    delete this._cells[cell.id];

    // remove option
    this.question.deleteOptions({ id: cell.id }, { push: 'none' });

    // push
    this.notifyChangeThruTree([], setOptions);
  }
}
